import { useEffect, useState } from "react";
import http from "../../commonFunctions/httpClient";
import { getLocalValue, LoacalVariables } from "../../commonFunctions/CommonFunctions";
import { triggerVendorOrdersRefresh, useVendorOrdersHub } from "../../commonFunctions/vendorOrdersHub";

export interface VendorNotification {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
  // The order this notification is about, when the API includes its id
  orderId: string;
}

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const ORDER_KEY_PATTERN = /(order|booking|reference|entity|related|target)/i;

// Looks through a notification's fields (and any nested / JSON-string data) for the id of its order
const findOrderId = (value: any, depth = 0): string => {
  if (!value || typeof value !== "object" || depth > 2) return "";
  for (const [key, field] of Object.entries(value)) {
    if (typeof field === "string" && ORDER_KEY_PATTERN.test(key)) {
      const match = field.match(UUID_PATTERN);
      if (match) return match[0];
    }
  }
  for (const field of Object.values(value)) {
    if (field && typeof field === "object") {
      const found = findOrderId(field, depth + 1);
      if (found) return found;
    } else if (typeof field === "string" && field.trim().startsWith("{")) {
      try {
        const found = findOrderId(JSON.parse(field), depth + 1);
        if (found) return found;
      } catch {
        // not JSON
      }
    }
  }
  return "";
};

// Where tapping a notification should go: the order's page, or the orders list when the
// notification doesn't say which order; "" when it isn't about an order
export const getNotificationPath = (notification: VendorNotification): string => {
  const text = `${notification.title} ${notification.body}`;
  if (!notification.orderId && !/order|booking/i.test(text)) return "";

  if (getLocalValue(LoacalVariables.UserType) === "pharmacy") {
    if (!notification.orderId) return "/pharmacy-orders/list";
    return /prescription/i.test(text)
      ? `/prescription-order-details/${notification.orderId}`
      : `/pharmacy-order-details/${notification.orderId}`;
  }
  return notification.orderId ? `/lab-orders/list/view/${notification.orderId}` : "/lab-orders/list";
};

interface NotificationState {
  items: VendorNotification[];
  loading: boolean;
  error: boolean;
  // List URL the items were loaded from, i.e. which user they belong to
  owner: string;
}

// One shared list so the header bell and the notification drawer always agree
let state: NotificationState = { items: [], loading: false, error: false, owner: "" };
const subscribers = new Set<(s: NotificationState) => void>();

const setState = (patch: Partial<NotificationState>) => {
  state = { ...state, ...patch };
  subscribers.forEach((fn) => fn(state));
};

// The API sends UTC times without a "Z" (e.g. "2026-10-12T09:33:22"), which browsers read as local
// time and so show hours behind. Times with no timezone marker are treated as UTC.
const toUtcIso = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";
  const hasZone = /(?:z|[+-]\d{2}(?::?\d{2})?)$/i.test(text);
  return hasZone ? text : `${text.replace(" ", "T")}Z`;
};

// Labs and pharmacies have separate notification endpoints
const getListUrl = () => {
  const userId = getLocalValue(LoacalVariables.UserId);
  if (!userId) return "";
  return getLocalValue(LoacalVariables.UserType) === "pharmacy"
    ? `Notification/vendor/${userId}`
    : `Notification/Lab/${userId}`;
};

let inFlight: Promise<void> | null = null;

// Overlapping calls (bell + drawer reacting to the same push) share one request
export const refreshNotifications = (showLoading = false) => {
  if (!inFlight) {
    inFlight = loadNotifications(showLoading).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};

const loadNotifications = async (showLoading: boolean) => {
  const url = getListUrl();
  if (!url) return;
  if (showLoading) setState({ loading: true, error: false });
  try {
    // Time limit so one stuck request can't block later refreshes; the changing query value
    // stops a browser / proxy cache from handing back an old copy of the list
    const response: any = await http.get(url, { timeout: 15000, params: { _: Date.now() } });
    const data: any[] = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    const items: VendorNotification[] = data
      .map((n) => ({
        id: n.id,
        title: n.title || "",
        body: n.body || "",
        isRead: Boolean(n.isRead),
        createdAt: toUtcIso(n.createdAt),
        orderId: findOrderId(n),
      }))
      // Newest first
      .sort((a, b) => (new Date(b.createdAt).getTime() || 0) - (new Date(a.createdAt).getTime() || 0));
    // A notification we haven't had before means an order changed: reload the order lists too
    const knownIds = new Set(state.items.map((n) => n.id));
    const hasNew = state.owner === url && items.some((n) => !knownIds.has(n.id));
    setState({ items, loading: false, error: false, owner: url });
    if (hasNew) triggerVendorOrdersRefresh();
  } catch (e) {
    console.error("Failed to load notifications:", e);
    setState({ loading: false, error: state.items.length === 0 });
  }
};

const setReadLocally = (ids: string[], isRead: boolean) =>
  setState({ items: state.items.map((n) => (ids.includes(n.id) ? { ...n, isRead } : n)) });

// Optimistic: shows as read immediately, rolls back if the API call fails
export const markNotificationRead = async (id: string) => {
  const target = state.items.find((n) => n.id === id);
  if (!target || target.isRead) return true;
  setReadLocally([id], true);
  try {
    await http.put(`Notification/read/${id}`, {});
    return true;
  } catch (e) {
    console.error("Failed to mark notification as read:", e);
    setReadLocally([id], false);
    return false;
  }
};

// The API marks one notification at a time, so this sends one request per unread item
export const markAllNotificationsRead = async () => {
  const unreadIds = state.items.filter((n) => !n.isRead).map((n) => n.id);
  if (unreadIds.length === 0) return 0;
  setReadLocally(unreadIds, true);
  const results = await Promise.allSettled(unreadIds.map((id) => http.put(`Notification/read/${id}`, {})));
  const failedIds = unreadIds.filter((_, i) => results[i].status === "rejected");
  if (failedIds.length) {
    console.error(`Failed to mark ${failedIds.length} notification(s) as read`);
    setReadLocally(failedIds, false);
  }
  return failedIds.length;
};

// Safety net for when the live hub connection is down: reload the list every 10 seconds while the
// app is open, and again as soon as the tab becomes visible. Runs once for all subscribers.
const POLL_INTERVAL_MS = 10000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const pollIfVisible = () => {
  if (document.visibilityState === "visible") refreshNotifications();
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(pollIfVisible, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", pollIfVisible);
  window.addEventListener("focus", pollIfVisible);
};

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  document.removeEventListener("visibilitychange", pollIfVisible);
  window.removeEventListener("focus", pollIfVisible);
};

export const useVendorNotifications = () => {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    // Drop a previous user's list (same tab, after logout / login)
    if (state.owner && state.owner !== getListUrl()) {
      state = { items: [], loading: false, error: false, owner: "" };
      setSnapshot(state);
    }
    subscribers.add(setSnapshot);
    // First subscriber loads the list
    if (subscribers.size === 1) refreshNotifications(state.items.length === 0);
    startPolling();
    return () => {
      subscribers.delete(setSnapshot);
      if (subscribers.size === 0) stopPolling();
    };
  }, []);

  // New notifications pushed live by the backend
  useVendorOrdersHub(() => refreshNotifications());

  return {
    notifications: snapshot.items,
    unreadCount: snapshot.items.filter((n) => !n.isRead).length,
    loading: snapshot.loading,
    // True once the list has loaded successfully at least once (even if it is empty)
    loaded: snapshot.owner !== "",
    error: snapshot.error,
    refresh: refreshNotifications,
    markRead: markNotificationRead,
    markAllRead: markAllNotificationsRead,
  };
};
